import {
  BarChartOutlined,
  CreditCardOutlined,
  HomeOutlined,
  NotificationOutlined,
  SettingOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { extractSectionFromPath } from '@supplement-bacon/react-toolkit/utils'
import { Flex, Layout, Menu, theme } from 'antd'
import { ItemType, MenuItemType } from 'antd/es/menu/interface'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSettingMenuItems } from '@hooks/useSettingMenuItems'
import { useHasAccess } from '../../components/HasAccess'
import { useTheme } from '../../contexts/ThemeContext'
import Tenant from '../../modules/tenant/Tenant'
import { Permission } from '../../utils/permissions'
import RightHeader from './RightHeader'

const { Header } = Layout

interface Props {
  subItems?: ItemType<MenuItemType>[] | undefined
}

const MainHeader = ({ subItems }: Props) => {
  const { token } = theme.useToken()
  const { mode } = useTheme()
  const { pathname } = useLocation()
  const { hasAccess } = useHasAccess()
  const { flatChildren: settingsChildren } = useSettingMenuItems()

  const [selectedKey, setSelectedKey] = useState<string>('events')
  const [selectedSubKey, setSelectedSubKey] = useState<string>('contacts')

  const items = [
    {
      key: 'overview',
      label: <Link to={'/overview'}>Overview</Link>,
      icon: <BarChartOutlined />,
      title: 'Overview',
      hasAccess: hasAccess(Permission.viewOverview),
    },
    {
      key: 'users',
      label: <Link to={'/users'}>CRM</Link>,
      icon: <UserOutlined />,
      title: 'CRM',
      hasAccess: hasAccess(Permission.listUser),
    },
    {
      key: 'venues',
      label: <Link to={'/venues'}>Venues</Link>,
      icon: <HomeOutlined />,

      title: 'Venues',
      hasAccess: hasAccess(Permission.listVenue),
    },
    {
      key: 'marketing',
      label: <Link to={'/marketing'}>Marketing</Link>,
      icon: <NotificationOutlined />,
      title: 'Marketing',
      // `list loyaltyTier` is deliberately absent. Business holds it so the
      // availability viewer can render its per-tier columns, but must not see
      // Marketing - and no role loses the section by dropping it here: every
      // holder of `list loyaltyTier` also holds `list push` (ACLHelper grants
      // both to exactly admin, marketing, communication, head-of-project,
      // membership and membership-program-manager).
      hasAccess: hasAccess([
        Permission.listPush,
        Permission.listReferral,
        Permission.listFeaturedVenue,
      ]),
    },
    {
      key: 'membership',
      label: <Link to={'/membership'}>Membership</Link>,
      icon: <StarOutlined />,
      title: 'Membership',
      hasAccess: hasAccess([
        Permission.manageRequestsMembership,
        Permission.manageMembersMembership,
        Permission.manageEventsMembership,
      ]),
    },
    {
      key: 'business',
      label: <Link to={'/business'}>Business</Link>,
      icon: <CreditCardOutlined />,
      title: 'Business',
      hasAccess: hasAccess([Permission.manageBusiness]),
    },
    {
      key: 'settings',
      label: <Link to={'/settings'}>Settings</Link>,
      icon: <SettingOutlined />,
      title: 'Settings',
      // Shown when at least one settings page is actually reachable, rather
      // than from a hand-kept list of permissions — which is how business
      // ended up with no route into Settings while owning three pages there.
      hasAccess: settingsChildren.length > 0,
    },
  ]

  const headerStyle: React.CSSProperties = {
    minWidth: 900,
    width: '100%',
    backgroundColor: token.colorBgContainer,
    justifyContent: 'space-between',
    display: 'flex',
    paddingRight: 20,
    paddingLeft: 20,
  }

  const subHeaderStyle: React.CSSProperties = {
    backgroundColor: mode == 'dark' ? '#303030' : '#ececec',
    justifyContent: 'space-between',
    display: 'flex',
    paddingRight: 20,
    paddingLeft: 20,
    height: 50,
    lineHeight: '50px',
  }

  useEffect(() => {
    setSelectedKey(extractSectionFromPath(pathname, 1))
    setSelectedSubKey(extractSectionFromPath(pathname, 2))
  }, [pathname])

  return (
    <div className={'fixed z-20 w-full'}>
      <Header style={headerStyle}>
        <Flex className="space-between w-full" gap={8}>
          <div className="logo-container">
            <Link to="/">
              <Tenant.Avatar />
            </Link>
          </div>
          <Menu
            style={{ width: '100%', border: 'none', minWidth: 700 }}
            mode="horizontal"
            selectedKeys={[selectedKey]}
            items={items.filter((item) => item.hasAccess)}
          />

          <RightHeader />
        </Flex>
      </Header>
      {subItems && (
        <Header style={subHeaderStyle}>
          <div
            style={{
              maxWidth: 1300,
              width: '100%',
              margin: 'auto',
            }}
          >
            <Menu
              style={{
                background: 'transparent',
                border: 'none',
              }}
              className="d-flex-lg"
              mode="horizontal"
              selectedKeys={[selectedSubKey]}
              items={subItems}
            />
          </div>
        </Header>
      )}
    </div>
  )
}

export default MainHeader
